import { Injectable } from '@angular/core';
import { Route, Router, Routes } from '@angular/router';
import { makeAuthService } from 'src/data/services/auth/auth-service-factory';
import { IAuthService } from 'src/data/services/auth/auth-service';

interface RouteAccessEntry {
  url: string;
}

@Injectable({
  providedIn: 'root',
})
export class RouteAccessService {
  private authService: IAuthService = makeAuthService();
  private permissionCache = new Map<string, Promise<boolean>>();
  private readonly routeAccessOrder: RouteAccessEntry[] = [
    { url: '/home' },
    { url: '/attendances' },
    { url: '/appointments' },
    { url: '/reports' },
    { url: '/priorities' },
    { url: '/locations' },
    { url: '/clients' },
    { url: '/resources' },
    { url: '/resolutions' },
    { url: '/services' },
    { url: '/links/entities' },
    { url: '/service-groups' },
    { url: '/attendants' },
  ];

  constructor(private readonly router: Router) {}

  async hasPermission(permission?: string): Promise<boolean> {
    if (!permission) {
      return true;
    }

    const cachedPermission = this.permissionCache.get(permission);

    if (cachedPermission) {
      return cachedPermission;
    }

    const permissionPromise = this.authService
      .hasPermission(permission)
      .catch(() => false);

    this.permissionCache.set(permission, permissionPromise);

    return permissionPromise;
  }

  async getFallbackUrl(
    excludedUrls: string[] = [],
    preferredUrl?: string,
  ): Promise<string> {
    const excludedUrlSet = new Set(excludedUrls.map((url) => this.normalizeUrl(url)));
    const normalizedPreferredUrl = preferredUrl
      ? this.normalizeUrl(preferredUrl)
      : null;

    if (
      normalizedPreferredUrl &&
      !excludedUrlSet.has(normalizedPreferredUrl) &&
      (await this.canAccessUrl(normalizedPreferredUrl))
    ) {
      return normalizedPreferredUrl;
    }

    for (const entry of this.routeAccessOrder) {
      const normalizedUrl = this.normalizeUrl(entry.url);

      if (excludedUrlSet.has(normalizedUrl)) {
        continue;
      }

      if (await this.canAccessUrl(normalizedUrl)) {
        return normalizedUrl;
      }
    }

    return '/appointments';
  }

  private async canAccessUrl(url: string): Promise<boolean> {
    const permission = this.findPermissionByUrl(url);
    const routeExists = this.routeExists(url);

    if (!routeExists) {
      return false;
    }

    return this.hasPermission(permission);
  }

  async canAccessRoute(url: string, permission?: string): Promise<boolean> {
    return this.hasPermission(permission ?? this.findPermissionByUrl(url));
  }

  private findPermissionByUrl(url: string): string | undefined {
    const normalizedUrl = this.normalizeUrl(url);
    const segments = normalizedUrl.split('/').filter(Boolean);

    return this.findPermissionInRoutes(this.router.config, segments);
  }

  private findPermissionInRoutes(
    routes: Routes,
    segments: string[],
  ): string | undefined {
    for (const route of routes) {
      const match = this.matchRoute(route, segments);

      if (!match.matched) {
        continue;
      }

      if (!match.remainingSegments.length) {
        return route.data?.['permission'] as string | undefined;
      }

      if (route.children?.length) {
        const childPermission = this.findPermissionInRoutes(
          route.children,
          match.remainingSegments,
        );

        if (childPermission !== undefined) {
          return childPermission;
        }
      }
    }

    return undefined;
  }

  private routeExists(url: string): boolean {
    const normalizedUrl = this.normalizeUrl(url);
    const segments = normalizedUrl.split('/').filter(Boolean);

    return this.routeExistsInRoutes(this.router.config, segments);
  }

  private routeExistsInRoutes(routes: Routes, segments: string[]): boolean {
    for (const route of routes) {
      const match = this.matchRoute(route, segments);

      if (!match.matched) {
        continue;
      }

      if (!match.remainingSegments.length) {
        return true;
      }

      if (route.children?.length) {
        const childExists = this.routeExistsInRoutes(
          route.children,
          match.remainingSegments,
        );

        if (childExists) {
          return true;
        }
      }
    }

    return false;
  }

  private matchRoute(
    route: Route,
    segments: string[],
  ): { matched: boolean; remainingSegments: string[] } {
    const routePath = route.path ?? '';
    const routeSegments = routePath.split('/').filter(Boolean);

    if (!routeSegments.length) {
      return {
        matched: true,
        remainingSegments: segments,
      };
    }

    if (routeSegments.length > segments.length) {
      return {
        matched: false,
        remainingSegments: segments,
      };
    }

    const matched = routeSegments.every((routeSegment, index) => {
      const currentSegment = segments[index];

      if (routeSegment === '**') {
        return true;
      }

      if (routeSegment.startsWith(':')) {
        return !!currentSegment;
      }

      return routeSegment === currentSegment;
    });

    if (!matched) {
      return {
        matched: false,
        remainingSegments: segments,
      };
    }

    if (route.pathMatch === 'full' && routeSegments.length !== segments.length) {
      return {
        matched: false,
        remainingSegments: segments,
      };
    }

    return {
      matched: true,
      remainingSegments:
        routeSegments.includes('**') ? [] : segments.slice(routeSegments.length),
    };
  }

  private normalizeUrl(url: string): string {
    const [pathWithoutQuery] = url.split('?');
    const [pathWithoutFragment] = pathWithoutQuery.split('#');
    const normalizedPath = pathWithoutFragment.replace(/\/+$/, '');

    return normalizedPath || '/';
  }
}
